import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import AutoLockSubsection from 'app/pages/loanApplication/AutoLockSubsection';
import {
  AutoLockFormsLevelType,
  BorrowersSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import useEnsureChannelMessagesLoaded from 'app/shared/components/TeamChat/hooks/useEnsureChannelMessagesLoaded';
import useLoanChatTargets from 'app/shared/components/TeamChat/hooks/useLoanChatTargets';
import {
  getAreaIdsByNameAndLevel,
  getNavNeededState,
  mergeNavNeededMaps,
} from 'app/shared/componentsRestyled/common/NavNeededIndicator/NavNeededIndicator.utils';
import { NoEntitiesFoundIcon } from 'assets/icons';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import { MAX_BORROWERS_COUNT, MAX_NBE_COUNT } from 'constants/loanApplication';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useIsCrmEnabled from 'hooks/useIsCrmEnabled';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import useTableCellPendingChangeAuthors from 'hooks/useTableCellPendingChangeAuthors';
import { calculatePendingChanges } from 'utils/pendingChanges';

import {
  getBorrowerCrmContactsAction,
  getBorrowersTableDataAction,
  getNonBorrowersTableDataAction,
  getPropertiesActionV2,
  getResidencesAction,
} from 'store/loanApplications/actions';
import {
  selectBorrowerFieldsVisibilityBySideData,
  selectBorrowersPendingChanges,
  selectDeletedAndNewBorrowers,
  selectDeletedAndNewNonBorrowers,
  selectIncompleteLinkedNavByBorrower,
  selectIncompleteLinkedNavLoanScoped,
  selectNonBorrowersPendingChanges,
  selectOrderedBorrowersTableData,
  selectOrderedNonBorrowersTableData,
  selectSubjectPropertyForResidence,
} from 'store/loanApplications/selectors';

import { getNbeTableConfig, getTableConfig } from './Borrower.config';
import { sectionStyles, tableWrapperStyles, wrapperStyles } from './Borrower.styles';
import EditEntityModal from './EditEntityModal';
import { normalizePendingChanges } from './EditEntityModal/Content/MainContent/MainContent.utils';
import {
  EDIT_ENTITY_DEFAULT_SECTION,
  ENTITY_TYPE_HREF,
  NEW_ENTITY,
} from './EditEntityModal/EditEntityModal.constants';
import useHasPosDomain from './hooks/useHasPosDomain';
import useNbeLandingRedirect from './hooks/useNbeLandingRedirect';

const Borrowers = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const history = useHistory();
  const { tab, applicationId, subtab } = useParams();

  const [isError, setIsError] = useState(false);

  const isCrmEnabled = useIsCrmEnabled();
  const hasPosDomain = useHasPosDomain();
  const chatTargets = useLoanChatTargets(applicationId);

  useEnsureChannelMessagesLoaded(applicationId);

  const borrowersTableData = useSelector(selectOrderedBorrowersTableData);
  const nonBorrowersTableData = useSelector(selectOrderedNonBorrowersTableData);
  const borrowersPendingChanges = useSelector(selectBorrowersPendingChanges);
  const nonBorrowersPendingChanges = useSelector(selectNonBorrowersPendingChanges);
  const { deletedBorrowers, newBorrowers } = useSelector(selectDeletedAndNewBorrowers);
  const { deletedNonBorrowers, newNonBorrowers } = useSelector(selectDeletedAndNewNonBorrowers);
  const subjectPropertyForResidence = useSelector(selectSubjectPropertyForResidence);
  const navNeededByBorrower = useSelector(selectIncompleteLinkedNavByBorrower());
  const navNeededLoanScoped = useSelector(selectIncompleteLinkedNavLoanScoped());
  const {
    positiveRentalHistory: isPositiveRentalHistoryVisible,
    caivrsNumber: isCaivrsNumberVisible,
  } = useSelector(selectBorrowerFieldsVisibilityBySideData);

  // Roll each borrower's incomplete checklist-linked fields up to the borrower
  // SUBSECTION area (excludes non-borrowing entities) so each row's opener shows
  // how many fields THAT borrower still needs, without opening the modal (DEV-4386).
  // Per-borrower (DEV-4810) — the loan-wide roll-up would stamp the same total on
  // every row. Loan-scoped items have no owning borrower, so they DO apply to every row.
  const borrowerNeededAreaIds = useMemo(
    () =>
      getAreaIdsByNameAndLevel(
        BorrowersSubsectionNames.BORROWERS,
        AutoLockFormsLevelType.SUBSECTION,
      ),
    [],
  );
  const borrowerNeededCountById = useMemo(
    () =>
      (borrowersTableData || []).reduce((acc, { id }) => {
        const borrowerNavMap = mergeNavNeededMaps(navNeededLoanScoped, navNeededByBorrower[id]);

        acc[id] = getNavNeededState(borrowerNavMap, borrowerNeededAreaIds).count;

        return acc;
      }, {}),
    [borrowersTableData, navNeededByBorrower, navNeededLoanScoped, borrowerNeededAreaIds],
  );

  const getCellPendingChangeAuthors = useTableCellPendingChangeAuthors();

  const safeBorrowersTableData = borrowersTableData || [];
  const safeNonBorrowersTableData = nonBorrowersTableData || [];
  const entitiesData =
    subtab === ENTITY_TYPE_HREF.BORROWER ? safeBorrowersTableData : safeNonBorrowersTableData;
  const subjectPropertyAddressId = subjectPropertyForResidence?.addressId;
  const isMaxNbeCount = safeNonBorrowersTableData.length >= MAX_NBE_COUNT;

  useNbeLandingRedirect({ isLoading, nonBorrowers: nonBorrowersTableData });

  const normalizedBorrowersPendingChanges = normalizePendingChanges({
    initialPendingChanges: borrowersPendingChanges,
    deletedEntities: deletedBorrowers,
    newEntities: newBorrowers,
    isBorrowerFlow: true,
    subjectPropertyAddressId,
    isPositiveRentalHistoryVisible,
    isCaivrsNumberVisible,
  });

  const normalizedNonBorrowersPendingChanges = normalizePendingChanges({
    initialPendingChanges: nonBorrowersPendingChanges,
    deletedEntities: deletedNonBorrowers,
    newEntities: newNonBorrowers,
  });

  const borrowerEntityIds = useMemo(
    () => (borrowersTableData || []).map(({ id }) => id),
    [borrowersTableData],
  );

  useEffectSafeStrictMode(() => {
    if (isCrmEnabled && borrowerEntityIds.length) {
      dispatch(getBorrowerCrmContactsAction({ borrowerIds: borrowerEntityIds }));
    }
  }, [isCrmEnabled, dispatch, borrowerEntityIds]);

  const nonBorrowerEntityIds = useMemo(
    () => (nonBorrowersTableData || []).map(({ id }) => id),
    [nonBorrowersTableData],
  );

  const borrowersCustomFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.BORROWER,
    entityIds: borrowerEntityIds,
  });

  const nonBorrowersCustomFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.NBE,
    entityIds: nonBorrowerEntityIds,
  });

  const borrowersPendingChangesCounts = _.sum(
    Object.values(
      calculatePendingChanges(
        _.merge({}, normalizedBorrowersPendingChanges, borrowersCustomFieldsPendingChanges),
        true,
      ),
    ),
  );

  const nonBorrowersPendingChangesCounts = _.sum(
    Object.values(
      calculatePendingChanges(
        _.merge({}, normalizedNonBorrowersPendingChanges, nonBorrowersCustomFieldsPendingChanges),
        true,
      ),
    ),
  );

  const tableConfig = getTableConfig({
    type: ENTITY_TYPE_HREF.BORROWER,
    pageData: {
      applicationId,
      tab,
      chatTargets,
    },
    entitiesData: safeBorrowersTableData,
    setIsLoading,
    isCrmEnabled,
    neededCountById: borrowerNeededCountById,
    hasPosDomain,
  });

  const nbeTableConfig = getNbeTableConfig({
    type: ENTITY_TYPE_HREF.NON_BORROWER,
    pageData: {
      applicationId,
      tab,
    },
    entitiesData: safeNonBorrowersTableData,
    setIsLoading,
  });

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setIsError(false);

    dispatch(getResidencesAction({ applicationId }));
    dispatch(getPropertiesActionV2({ applicationId, withSubjectProperty: true }));

    Promise.all([
      dispatch(getBorrowersTableDataAction({ applicationId })),
      dispatch(getNonBorrowersTableDataAction({ applicationId })),
    ])
      .catch(() => setIsError(true))
      .finally(() => {
        setIsLoading(false);
      });
  }, [applicationId, dispatch, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  const onAddBorrowerClick = useCallback(() => {
    const pathname = LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab,
      subtab: ENTITY_TYPE_HREF.BORROWER,
      section: EDIT_ENTITY_DEFAULT_SECTION[ENTITY_TYPE_HREF.BORROWER],
      entityId: NEW_ENTITY,
    });

    history.push(pathname);
  }, [applicationId, history, tab]);

  const onAddNBEClick = useCallback(() => {
    const pathname = LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab,
      subtab: ENTITY_TYPE_HREF.NON_BORROWER,
      section: EDIT_ENTITY_DEFAULT_SECTION[ENTITY_TYPE_HREF.NON_BORROWER],
      entityId: NEW_ENTITY,
    });

    history.push(pathname);
  }, [applicationId, history, tab]);

  return (
    <ErrorHandlingLayout isError={isError} onClick={fetchData}>
      <Box sx={wrapperStyles}>
        <AutoLockSubsection
          areaName={BorrowersSubsectionNames.BORROWERS}
          levelType={AutoLockFormsLevelType.SUBSECTION}
        >
          <Box sx={sectionStyles}>
            <TableCDS
              withNoAvailableMessage
              config={tableConfig}
              customStyles={tableWrapperStyles}
              data={safeBorrowersTableData}
              getCellPendingChangeAuthors={getCellPendingChangeAuthors}
              isLoading={isLoading || isError}
              noAvailableMessageIcon={NoEntitiesFoundIcon}
              noAvailableMessageText="No borrowers found"
              pendingChangesCount={borrowersPendingChangesCounts}
              skeletonRows={2}
              title="Borrower(s)"
              withAddButton={safeBorrowersTableData.length < MAX_BORROWERS_COUNT}
              onAddClick={onAddBorrowerClick}
            />
          </Box>
        </AutoLockSubsection>

        <AutoLockSubsection
          areaName={BorrowersSubsectionNames.NON_BORROWERS}
          levelType={AutoLockFormsLevelType.SUBSECTION}
        >
          <Box sx={sectionStyles}>
            <TableCDS
              withAddButton
              withNoAvailableMessage
              config={nbeTableConfig}
              customStyles={tableWrapperStyles}
              data={safeNonBorrowersTableData}
              getCellPendingChangeAuthors={getCellPendingChangeAuthors}
              isAddButtonDisabled={isMaxNbeCount}
              isLoading={isLoading || isError}
              noAvailableMessageIcon={NoEntitiesFoundIcon}
              noAvailableMessageText="No non-borrowing entities found"
              pendingChangesCount={nonBorrowersPendingChangesCounts}
              skeletonRows={2}
              title="Non Borrowing Entities"
              onAddClick={onAddNBEClick}
            />
          </Box>
        </AutoLockSubsection>

        <EditEntityModal entitiesData={entitiesData} isLoading={isLoading} />
      </Box>
    </ErrorHandlingLayout>
  );
};

Borrowers.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Borrowers);
